import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { EmptyLeaderboard } from '../../components/Dashboard/EmptyLeaderBoard/EmptyLeaderboard';
import { EllipsisDots } from '../../components/Dashboard/Leaderboard/EllipsisDots/EllipsisDots';
import { LeaderboardItem } from '../../components/Dashboard/Leaderboard/LeaderboardItem/LeaderboardItem';
import { PeriodSelector } from '../../components/Dashboard/Leaderboard/PeriodSelector/PeriodSelector';
import { SeeMoreButton } from '../../components/Dashboard/Leaderboard/SeeMoreButton/SeeMoreButton';
import Header from '../../components/shared/Header/Header';
import { Pagination } from '../../components/shared/Pagination/Pagination';
import { IsMobile } from '../../helpers/IsMobile';
import { getLeaderBoardList } from '../../redux/actions/dashboardActions';
import { selectLeaderboardList, selectLeaderboardListLoading } from '../../selectors/dashboardSelectors';
import { selectCurrentOrganization } from '../../selectors/organizationSelectors';

export const LeaderboardPage = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const entriesPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [displayCount, setDisplayCount] = useState(entriesPerPage);
  const [period, setPeriod] = useState('week');
  const leaderboardList = useSelector(selectLeaderboardList);
  const loading = useSelector((state) => state.globalReducer.loading);
  const leaderboardLoading = useSelector(selectLeaderboardListLoading);
  const currentOrganization = useSelector(selectCurrentOrganization);
  const currentUser = useSelector((state) => state.authReducer.currentUser);
  const isMobile = IsMobile();

  const totalPages = Math.ceil(leaderboardList.length / entriesPerPage);
  const currentData = useMemo(() => {
    if (isMobile) {
      return leaderboardList.slice(0, displayCount);
    }

    return leaderboardList.slice((currentPage - 1) * entriesPerPage, currentPage * entriesPerPage);
  }, [leaderboardList, currentPage, displayCount, isMobile]);

  const handleSeeMore = () => {
    setDisplayCount((prevCount) => prevCount + entriesPerPage);
  };

  // Check if current user is not in the current data but exists in the list
  const shouldShowCurrentUser =
    currentUser &&
    !currentData.find((item) => item.user.id === currentUser?.user?.id) &&
    leaderboardList.find((item) => item.user.id === currentUser?.user?.id);

  // Find current user's item and rank
  const getUserData = () => {
    if (!shouldShowCurrentUser) return null;
    const userItem = leaderboardList.find((item) => item.user.id === currentUser?.user?.id);
    const userRank = leaderboardList.findIndex((item) => item.user.id === currentUser?.user?.id) + 1;
    return { userItem, userRank };
  };

  useEffect(() => {
    if (currentOrganization) {
      dispatch(getLeaderBoardList({ filter: period, organization_id: currentOrganization.id }));
    }
  }, [dispatch, period, currentOrganization?.id]);

  const renderLeaderboardEmptyState = () => {
    if (!leaderboardLoading && currentData.length === 0) {
      return <EmptyLeaderboard />;
    }
  };

  return (
    <>
      <div className="max-md:hidden">
        <Header title={t('headers.leaderboard')} backButton={t('headers.backToDashboard')} />
      </div>
      <div className="flex items-center justify-between h-16 px-3 py-4 bg-white md:hidden">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 p-0 mt-1 text-xs font-medium text-gray-500 bg-transparent border-none"
        >
          <ArrowLeft />
        </button>
        <p className="text-xl font-semibold text-gray-900">{t('headers.leaderboard')}</p>
        <span />
      </div>
      <div className="h-[calc(100vh-84px)] max-md:h-[calc(100vh-134px)] overflow-y-scroll">
        <div className="flex items-center justify-center p-[26px]">
          <div className="flex flex-col w-full gap-3 p-3 bg-white rounded-xl shadow-card lg:w-1/2">
            <div className="flex flex-row items-center justify-between px-3">
              <div className="flex flex-col">
                <p className="text-lg font-semibold text-gray-900">{t('headers.leaderboard')}</p>
                <p className="text-xs text-gray-600">{t('referrals.title')}</p>
              </div>
              <PeriodSelector period={period} setPeriod={setPeriod} />
            </div>
            {!leaderboardLoading && !loading && currentData.length > 0 ? (
              <>
                <div className="space-y-3">
                  {currentData.map((item, index) => (
                    <LeaderboardItem
                      key={item.user.id || index}
                      item={item}
                      rank={(currentPage - 1) * entriesPerPage + index + 1}
                      currentUserId={currentUser?.user?.id}
                    />
                  ))}

                  {shouldShowCurrentUser && (
                    <>
                      <EllipsisDots />
                      {(() => {
                        const { userItem, userRank } = getUserData();
                        return (
                          <LeaderboardItem
                            key={userItem.user.id}
                            item={userItem}
                            rank={userRank}
                            currentUserId={currentUser?.user?.id}
                          />
                        );
                      })()}
                    </>
                  )}
                </div>
                <div className="flex justify-center max-md:hidden">
                  <Pagination totalPages={totalPages} currentPage={currentPage} setCurrentPage={setCurrentPage} />
                </div>
                {displayCount < leaderboardList.length && <SeeMoreButton onClick={handleSeeMore} isMobile />}
              </>
            ) : (
              renderLeaderboardEmptyState()
            )}
          </div>
        </div>
      </div>
    </>
  );
};
